import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarOff, Plus } from 'lucide-react';
import type { LeaveDto } from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useMenuPath } from '../lib/menu-path';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { SkeletonCard } from '../components/ui/Skeleton';
import { LeaveDrawer } from '../components/calendar/LeaveDrawer';
import { LEAVE_TYPE_LABEL, LeaveStatusChip } from '../components/calendar/LeaveStatusChip';
import { academyToday, dayCount, formatRange } from '../components/calendar/format';

export const Route = createFileRoute('/_authed/me/leave')({
  component: MyLeavePage,
});

function MyLeavePage() {
  const user = useAuthStore((s) => s.user);
  // Shown to staff who open this teacher page: point them to their own one.
  const leavePath = useMenuPath('/academics/leave');
  if (user && user.primaryRole !== 'teacher') {
    return (
      <ListShell title="My leave" description="This page is for teachers.">
        <EmptyState
          title="Leave is requested by teachers"
          description={leavePath ? `You review and record teacher leave in ${leavePath}.` : 'Nalims and admins review and record teacher leave.'}
          icon={<CalendarOff size={32} aria-hidden="true" />}
        />
      </ListShell>
    );
  }
  return <TeacherLeaveView />;
}

function TeacherLeaveView() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();
  const today = academyToday();

  const leavesQuery = useQuery({
    queryKey: ['calendar', 'leaves', 'mine'] as const,
    queryFn: ({ signal }) => apiRequest<{ items: LeaveDto[] }>('/api/calendar/leaves', { signal }),
  });

  const cancel = useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ ok: true }>(`/api/calendar/leaves/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: {} }),
    onSuccess: () => {
      toast('Leave cancelled.', { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Could not cancel leave.', { variant: 'error' }),
  });

  const onCancel = async (leave: LeaveDto) => {
    const ok = await confirm({
      title: 'Cancel this leave?',
      description: `${formatRange(leave.startDate, leave.endDate)} · your classes on these days will be expected again.`,
      confirmLabel: 'Cancel leave',
      cancelLabel: 'Keep it',
      variant: 'danger',
    });
    if (ok) cancel.mutate(leave.id);
  };

  const items = leavesQuery.data?.items ?? [];
  const upcoming = items.filter((l) => l.endDate >= today && (l.status === 'requested' || l.status === 'approved'));
  const past = items.filter((l) => !upcoming.includes(l)).reverse();

  return (
    <ListShell
      title="My leave"
      description="Request leave ahead of time. Once your nalim approves it, no attendance or reports are expected for those days — and your report streak is safe."
      primaryAction={
        <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => setDrawerOpen(true)}>
          Request leave
        </Button>
      }
    >
      {leavesQuery.isLoading ? (
        <div className="grid gap-3 md:grid-cols-2">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : leavesQuery.isError ? (
        <ErrorState
          title="Could not load your leave"
          description={leavesQuery.error instanceof ApiError ? leavesQuery.error.message : undefined}
          onRetry={() => void leavesQuery.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="No leave yet"
          description="When you need time off, request it here so your nalim can plan cover."
          icon={<CalendarOff size={32} aria-hidden="true" />}
          action={<Button onClick={() => setDrawerOpen(true)}>Request leave</Button>}
        />
      ) : (
        <div className="space-y-6">
          <LeaveSection title="Upcoming & pending" items={upcoming} today={today} onCancel={onCancel} empty="Nothing coming up." />
          {past.length > 0 ? <LeaveSection title="Earlier" items={past} today={today} onCancel={onCancel} /> : null}
        </div>
      )}
      <LeaveDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} mode="self" />
    </ListShell>
  );
}

function LeaveSection({
  title,
  items,
  today,
  onCancel,
  empty,
}: {
  title: string;
  items: LeaveDto[];
  today: string;
  onCancel: (l: LeaveDto) => void;
  empty?: string;
}) {
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold">{title}</h2>
      {items.length === 0 ? (
        <p className="text-sm text-[color:var(--color-text-muted)]">{empty}</p>
      ) : (
        <ul className="divide-y divide-dashed divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
          {items.map((l) => {
            const canCancel =
              l.status === 'requested' || (l.status === 'approved' && l.startDate > today);
            return (
              <li key={l.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="font-medium">
                    {formatRange(l.startDate, l.endDate)}{' '}
                    <span className="text-xs font-normal text-[color:var(--color-text-muted)]">
                      · {dayCount(l.startDate, l.endDate)} day{dayCount(l.startDate, l.endDate) === 1 ? '' : 's'} ·{' '}
                      {LEAVE_TYPE_LABEL[l.leaveType]}
                    </span>
                  </p>
                  {l.reason ? <p className="text-sm text-[color:var(--color-text-muted)]">{l.reason}</p> : null}
                  {l.decisionNote ? (
                    <p className="text-xs text-[color:var(--color-text-muted)]">Note from your nalim: {l.decisionNote}</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <LeaveStatusChip status={l.status} />
                  {canCancel ? (
                    <Button size="sm" variant="ghost" onClick={() => onCancel(l)}>
                      Cancel
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
